<script lang="ts">
  /**
   * The logbook: one row per flight, newest first.
   *
   * A list on a phone and a table on a laptop, from one markup: the row is a grid whose
   * template changes at the breakpoint, rather than two components that drift apart.
   */
  import DatePicker from './DatePicker.svelte';
  import LoopingPlane from './LoopingPlane.svelte';
  import { api } from './api.ts';
  import type { Aircraft, Flight } from './api.ts';
  import { duration, km, localDate, localTime, metres, number, place, weekday } from './format.ts';

  interface Props {
    aircraft: Aircraft[];
    onOpen: (id: string) => void;
  }

  let { aircraft, onOpen }: Props = $props();

  /**
   * Ten at a time, appended as you reach the bottom.
   *
   * Small on purpose: the page shows about that many rows, so the first request is exactly what
   * is needed to fill the screen and nothing more. A sentinel below the list is watched by an
   * IntersectionObserver — no scroll handler, so nothing runs on frames where nothing changed.
   */
  const PAGE = 10;

  const today = new Date();
  const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
  const daysAgo = (n: number): string =>
    isoDay(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - n)));

  type RangeId = 'all' | '7d' | '1m' | '1y' | 'custom';
  const RANGES: ReadonlyArray<{ id: RangeId; label: string; days: number | null }> = [
    { id: 'all', label: 'Alle', days: null },
    { id: '7d', label: '7 Tage', days: 7 },
    { id: '1m', label: '1 Monat', days: 30 },
    { id: '1y', label: '1 Jahr', days: 365 },
    { id: 'custom', label: 'Zeitraum', days: null },
  ];

  let selected = $state('');
  let range = $state<RangeId>('all');
  let customFrom = $state(daysAgo(30));
  let customTo = $state(isoDay(today));

  /** What actually goes on the wire. `all` sends nothing, which is not the same as sending a
   *  range that happens to cover everything — it lets the server skip the comparison entirely. */
  const window_ = $derived.by(() => {
    if (range === 'all') return { from: undefined, to: undefined };
    if (range === 'custom') return { from: customFrom, to: customTo };
    const days = RANGES.find((r) => r.id === range)?.days ?? 30;
    return { from: daysAgo(days), to: isoDay(today) };
  });

  let flights = $state<Flight[]>([]);
  let total = $state(0);
  let loading = $state(true);
  let loadingMore = $state(false);
  let error = $state<string | null>(null);
  let sentinel: HTMLDivElement | undefined = $state();

  /** Bumped whenever a filter changes, to discard answers from a query nobody is looking at. */
  let generation = 0;

  // The first page. Reading `selected` and `window_` here is what re-runs it on a filter change.
  $effect(() => {
    const params = { aircraft: selected, ...window_ };
    const mine = ++generation;
    const controller = new AbortController();
    loading = true;
    error = null;

    api
      .flights({ ...params, limit: PAGE, offset: 0, status: 'closed' }, controller.signal)
      .then((page) => {
        if (mine !== generation) return;
        flights = page.flights;
        total = page.total;
        loading = false;
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || mine !== generation) return;
        error = err instanceof Error ? err.message : String(err);
        loading = false;
      });

    return () => controller.abort();
  });

  async function loadMore(): Promise<void> {
    if (loadingMore || loading || flights.length >= total) return;
    const mine = generation;
    loadingMore = true;
    try {
      const page = await api.flights({
        aircraft: selected,
        ...window_,
        limit: PAGE,
        offset: flights.length,
        status: 'closed',
      });
      // A filter changed while this was in flight; its rows belong to a list nobody is looking
      // at, and appending them would mix two queries into one list.
      if (mine !== generation) return;
      flights = [...flights, ...page.flights];
      total = page.total;
    } catch {
      /* Leave what is already shown; the sentinel will try again on the next scroll. */
    } finally {
      loadingMore = false;
    }
  }

  $effect(() => {
    const el = sentinel;
    if (el === undefined) return;
    // rootMargin, so the next page is already arriving by the time the last row is reached
    // rather than after the scroll has stopped against the bottom.
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) void loadMore();
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  });

  const registrationOf = (id: string) =>
    aircraft.find((a) => a.id === id)?.registration ?? id;

  function select(id: string): void {
    selected = id;
  }
</script>

<div class="filters">
  <div class="chips" role="tablist" aria-label="Luftfahrzeug">
    <button
      role="tab"
      aria-selected={selected === ''}
      class:active={selected === ''}
      onclick={() => select('')}
      type="button">Alle</button
    >
    {#each aircraft as a (a.id)}
      <button
        role="tab"
        aria-selected={selected === a.id}
        class:active={selected === a.id}
        onclick={() => select(a.id)}
        type="button">{a.registration}</button
      >
    {/each}
  </div>

  <a class="csv" href={api.csvUrl({ aircraft: selected, ...window_ })} download>CSV</a>
</div>

<div class="filters ranges">
  <div class="chips" role="tablist" aria-label="Zeitraum">
    {#each RANGES as r (r.id)}
      <button
        role="tab"
        aria-selected={range === r.id}
        class:active={range === r.id}
        onclick={() => (range = r.id)}
        type="button">{r.label}</button
      >
    {/each}
  </div>

  {#if !loading && error === null}
    <span class="count">{flights.length} von {total}</span>
  {/if}
</div>

<!--
  A row of its own, not a third item in the row above.

  As a sibling of the range chips it was a third flex child competing for 390 px, and the
  measurement was blunt: the chips got 141 px of the 362 px they need. Two thirds of the range
  buttons were off-screen, the "bis" picker was cut in half, and nothing on screen said so.
-->
{#if range === 'custom'}
  <div class="filters">
    <div class="chips dates">
      <DatePicker label="von" bind:value={customFrom} max={customTo} />
      <DatePicker label="bis" bind:value={customTo} min={customFrom} />
    </div>
  </div>
{/if}

{#if error !== null}
  <p class="state bad">Flugbuch nicht ladbar: {error}</p>
{:else if loading && flights.length === 0}
  <p class="state"><LoopingPlane width={132} seconds={4} label="Lädt" /></p>
{:else if flights.length === 0}
  <p class="state">
    Noch keine Flüge aufgezeichnet. Sobald eines der konfigurierten Luftfahrzeuge im
    OGN-Feed auftaucht und startet, erscheint es hier.
  </p>
{:else}
  <ol class="list">
    {#each flights as flight (flight.id)}
      <li>
        <button class="row" onclick={() => onOpen(flight.id)} type="button">
          <span class="date">
            <span class="day num">{localDate(flight.takeoffT)}</span>
            <span class="weekday faint">{weekday(flight.takeoffT)}</span>
          </span>

          <span class="reg">
            {registrationOf(flight.aircraftId)}
            {#if flight.landingInferred}
              <span class="pill warn" title="Kein Landefix — aus dem Signalverlust geschlossen"
                >geschätzt</span
              >
            {/if}
          </span>

          <span class="times num">
            {localTime(flight.takeoffT)}–{localTime(flight.landingT)}
            <span class="dur">{duration(flight.durationS)}</span>
          </span>

          <span class="route">
            {place(flight.takeoffPlace, flight.takeoffPlaceCode)}
            {#if flight.landingPlaceCode !== flight.takeoffPlaceCode || flight.landingPlace !== flight.takeoffPlace}
              <span class="faint">→</span>
              {place(flight.landingPlace, flight.landingPlaceCode)}
            {/if}
          </span>

          <span class="score num">
            {#if flight.score !== null}
              <span class="free">{km(flight.score.freeDistanceKm, 0)}</span>
              {#if flight.score.faiValid}
                <span class="fai" title="FAI-Dreieck">△ {km(flight.score.faiTriangleKm, 0)}</span>
              {/if}
              <span class="pts faint">{number(flight.score.olcPlusPoints, 0)} P</span>
            {:else}
              <span class="faint">—</span>
            {/if}
          </span>

          <span class="alt num faint">{metres(flight.metrics?.maxAltM)}</span>
        </button>
      </li>
    {/each}
  </ol>

  <!-- Watched by the IntersectionObserver, and where the "loading more" plane appears so the
       list does not jump when a page lands. It replaced a pager that wanted a click per ten. -->
  <div class="sentinel" bind:this={sentinel}>
    {#if loadingMore}
      <LoopingPlane width={110} seconds={3.5} label="Lädt weitere Flüge" />
    {:else if flights.length >= total && total > PAGE}
      <span class="end">Alle {total} Flüge geladen</span>
    {/if}
  </div>
{/if}

<style>
  .filters {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 0.8rem;
  }

  .chips {
    display: flex;
    gap: 0.35rem;
    overflow-x: auto;
    scrollbar-width: none;
    flex: 1;
  }
  .chips::-webkit-scrollbar {
    display: none;
  }

  .chips button {
    min-height: 36px;
    padding: 0 0.8rem;
    border-radius: 999px;
    border: 1px solid var(--line);
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-dim);
    white-space: nowrap;
  }

  /*
   * On a phone the filters wrap instead of scrolling sideways.
   *
   * Sideways scrolling looked tidy and hid things: five registrations need 366 px and had 290,
   * so D-MTFA was simply not there, cut mid-word at an edge that reads as a broken layout
   * rather than as "there is more". A filter you cannot see is a filter you do not know exists.
   * Wrapping costs one line of height, which a phone has, and hides nothing.
   *
   * Touch targets go up with it. 36 px is fine for a mouse; this gets used one-handed on a
   * grass airfield, sometimes in gloves.
   */
  @media (max-width: 700px) {
    .chips {
      flex-wrap: wrap;
      overflow-x: visible;
    }
    /* Once the chips are two lines tall, centring leaves CSV and the count floating against
       nothing at half height, which reads as a mistake. Both belong on the first line. */
    .filters {
      align-items: flex-start;
    }
    .count {
      line-height: 36px;
    }
  }
  @media (pointer: coarse) {
    .chips button {
      min-height: 44px;
    }
    .csv {
      line-height: 44px;
    }
  }
  .chips button.active {
    background: var(--accent-soft);
    border-color: color-mix(in srgb, var(--accent) 45%, transparent);
    color: var(--accent);
  }

  .ranges {
    margin-top: -0.35rem;
  }

  .count {
    margin-left: auto;
    font-size: 0.78rem;
    color: var(--text-dim);
    font-variant-numeric: tabular-nums;
  }

  .dates {
    padding: 0.1rem 0.2rem;
  }

  .sentinel {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 3.5rem;
    color: var(--text-dim);
  }

  .end {
    font-size: 0.78rem;
  }

  .csv {
    padding: 0 0.75rem;
    line-height: 36px;
    border-radius: 999px;
    border: 1px solid var(--line);
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-dim);
  }
  .csv:hover {
    color: var(--accent);
    text-decoration: none;
  }

  .state {
    padding: 2rem 1rem;
    text-align: center;
    color: var(--text-dim);
  }
  .state.bad {
    color: var(--bad);
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .row {
    width: 100%;
    display: grid;
    gap: 0.15rem 0.7rem;
    padding: 0.7rem 0.85rem;
    text-align: left;
    background: var(--bg-raised);
    border: 1px solid var(--line);
    border-radius: var(--radius);
    transition: border-color 0.12s ease, transform 0.12s ease;

    /* Phone: two columns, four rows. Everything readable without sideways scrolling. */
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'date reg'
      'times score'
      'route alt';
  }
  .row:hover {
    border-color: var(--line-strong);
  }
  .row:active {
    transform: scale(0.995);
  }
  .row:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  .date {
    grid-area: date;
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
  }
  .day {
    font-weight: 600;
  }
  .weekday {
    font-size: 0.8rem;
  }

  .reg {
    grid-area: reg;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    justify-content: flex-end;
    font-weight: 600;
  }

  .times {
    grid-area: times;
    color: var(--text-dim);
    font-size: 0.9rem;
  }
  .dur {
    margin-left: 0.5rem;
    color: var(--text);
    font-weight: 600;
  }

  .route {
    grid-area: route;
    font-size: 0.85rem;
    color: var(--text-dim);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .score {
    grid-area: score;
    display: flex;
    gap: 0.55rem;
    justify-content: flex-end;
    align-items: baseline;
    font-size: 0.9rem;
  }
  .free {
    font-weight: 700;
    color: var(--accent);
  }
  .fai {
    color: var(--sky);
  }
  .pts {
    font-size: 0.8rem;
  }

  .alt {
    grid-area: alt;
    text-align: right;
    font-size: 0.82rem;
  }

  /*
   * Phone: drop the FAI triangle from the list, and stop the middle line breaking.
   *
   * Measured rather than guessed. The middle band needs 141 px for the times and 171 px for the
   * scores; the grid gives column one 138 px, so it wrapped — by three pixels — and every row
   * became four ragged lines instead of three. Shaving a gap would have fixed exactly this
   * string and broken again on a four-digit distance.
   *
   * So the FAI figure goes instead. On a phone the list answers "which flight was that", and
   * `154 km △ 154 km 200 P` reads as a repetition at this size; the triangle is one tap away on
   * the flight itself, where it is shown with its closure and turnpoints. Free distance and
   * points stay, because those are what the list is scanned for.
   */
  @media (max-width: 559px) {
    .fai {
      display: none;
    }
    .times,
    .score {
      white-space: nowrap;
    }
  }

  /* Laptop: one row, six columns — a real table, from the same markup. */
  @media (min-width: 800px) {
    .row {
      grid-template-columns: 11rem 8rem 13rem 1fr 12rem 6rem;
      grid-template-areas: 'date reg times route score alt';
      align-items: center;
    }
    .reg {
      justify-content: flex-start;
    }
  }

</style>
